import axios from 'axios';
import { API_ENDPOINTS } from '@pc/config';

// src/http/http-client.ts

// src/http/api-error.ts
var ApiError = class _ApiError extends Error {
  code;
  status;
  details;
  originalError;
  constructor(code, message, status, details, originalError) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
    this.details = details;
    this.originalError = originalError;
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, _ApiError);
    }
  }
  /**
   * Create ApiError from Axios error
   */
  static fromAxiosError(error) {
    const status = error.response?.status ?? 0;
    const data = error.response?.data;
    if (!error.response) {
      if (error.code === "ECONNABORTED") {
        return new _ApiError("TIMEOUT", "Request timeout", 0, void 0, error);
      }
      return new _ApiError("NETWORK_ERROR", "Network error", 0, void 0, error);
    }
    if (data?.error) {
      return new _ApiError(
        data.error.code,
        data.error.message,
        status,
        data.error.details,
        error
      );
    }
    const codeMap = {
      400: "VALIDATION_ERROR",
      401: "UNAUTHORIZED",
      403: "FORBIDDEN",
      404: "NOT_FOUND",
      409: "CONFLICT",
      422: "VALIDATION_ERROR",
      500: "INTERNAL_ERROR"
    };
    const code = codeMap[status] ?? "UNKNOWN";
    const message = error.message || "An error occurred";
    return new _ApiError(code, message, status, void 0, error);
  }
  /**
   * Check if error is of specific code
   */
  is(code) {
    return this.code === code;
  }
  /**
   * Check if error is unauthorized
   */
  isUnauthorized() {
    return this.code === "UNAUTHORIZED" || this.status === 401;
  }
  /**
   * Check if error is forbidden
   */
  isForbidden() {
    return this.code === "FORBIDDEN" || this.status === 403;
  }
  /**
   * Check if error is not found
   */
  isNotFound() {
    return this.code === "NOT_FOUND" || this.status === 404;
  }
  /**
   * Check if error is validation error
   */
  isValidationError() {
    return this.code === "VALIDATION_ERROR" || this.status === 422;
  }
  /**
   * Check if error is network error
   */
  isNetworkError() {
    return this.code === "NETWORK_ERROR" || this.code === "TIMEOUT";
  }
  /**
   * Get validation error for specific field
   */
  getFieldError(field) {
    return this.details?.[field];
  }
  /**
   * Convert to JSON
   */
  toJSON() {
    return {
      name: this.name,
      code: this.code,
      message: this.message,
      status: this.status,
      details: this.details
    };
  }
};

// src/http/http-client.ts
var HttpClient = class {
  client;
  tokenProvider = null;
  onUnauthorized = null;
  constructor(config) {
    this.client = axios.create({
      baseURL: config.baseUrl,
      timeout: config.timeout ?? 3e4,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...config.headers
      }
    });
    this.setupInterceptors();
  }
  /**
   * Set token provider for authentication
   */
  setTokenProvider(provider) {
    this.tokenProvider = provider;
  }
  /**
   * Set unauthorized callback
   */
  setOnUnauthorized(callback) {
    this.onUnauthorized = callback;
  }
  /**
   * Setup request/response interceptors
   */
  setupInterceptors() {
    this.client.interceptors.request.use(
      async (config) => {
        if (this.tokenProvider) {
          const token = await this.tokenProvider();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (axios.isAxiosError(error)) {
          const status = error.response?.status;
          error.response?.data;
          if (status === 401) {
            this.onUnauthorized?.();
          }
          throw ApiError.fromAxiosError(error);
        }
        throw error;
      }
    );
  }
  /**
   * GET request
   */
  async get(url, config) {
    const response = await this.client.get(url, config);
    return response.data.data;
  }
  /**
   * GET request with full response
   */
  async getFullResponse(url, config) {
    const response = await this.client.get(url, config);
    return response.data;
  }
  /**
   * POST request
   */
  async post(url, data, config) {
    const response = await this.client.post(url, data, config);
    return response.data.data;
  }
  /**
   * PUT request
   */
  async put(url, data, config) {
    const response = await this.client.put(url, data, config);
    return response.data.data;
  }
  /**
   * PATCH request
   */
  async patch(url, data, config) {
    const response = await this.client.patch(url, data, config);
    return response.data.data;
  }
  /**
   * DELETE request
   */
  async delete(url, config) {
    const response = await this.client.delete(url, config);
    return response.data.data;
  }
  /**
   * Get underlying Axios instance
   */
  getAxiosInstance() {
    return this.client;
  }
};

// src/http/request-queue.ts
var RequestQueue = class _RequestQueue {
  static STORAGE_KEY = "pc_request_queue";
  storage;
  queue = [];
  processing = false;
  constructor(storage) {
    this.storage = storage;
  }
  /**
   * Initialize queue from storage
   */
  async initialize() {
    const stored = await this.storage.getItem(_RequestQueue.STORAGE_KEY);
    if (stored) {
      try {
        this.queue = JSON.parse(stored);
      } catch {
        this.queue = [];
      }
    }
  }
  /**
   * Add request to queue
   */
  async enqueue(request) {
    const id = this.generateId();
    const queuedRequest = {
      ...request,
      id,
      createdAt: Date.now(),
      retryCount: 0,
      maxRetries: request.maxRetries ?? 3
    };
    this.queue.push(queuedRequest);
    await this.persist();
    return id;
  }
  /**
   * Remove request from queue
   */
  async dequeue(id) {
    this.queue = this.queue.filter((r) => r.id !== id);
    await this.persist();
  }
  /**
   * Get all queued requests
   */
  getAll() {
    return [...this.queue];
  }
  /**
   * Get pending count
   */
  getPendingCount() {
    return this.queue.length;
  }
  /**
   * Check if queue has pending requests
   */
  hasPending() {
    return this.queue.length > 0;
  }
  /**
   * Process queue with executor function
   */
  async process(executor, onError) {
    if (this.processing) {
      return { success: 0, failed: 0 };
    }
    this.processing = true;
    let success = 0;
    let failed = 0;
    const requests = [...this.queue];
    for (const request of requests) {
      try {
        await executor(request);
        await this.dequeue(request.id);
        success++;
      } catch (error) {
        request.retryCount++;
        if (request.retryCount >= request.maxRetries) {
          await this.dequeue(request.id);
          failed++;
          onError?.(request, error);
        } else {
          await this.persist();
        }
      }
    }
    this.processing = false;
    return { success, failed };
  }
  /**
   * Clear all queued requests
   */
  async clear() {
    this.queue = [];
    await this.persist();
  }
  /**
   * Persist queue to storage
   */
  async persist() {
    await this.storage.setItem(_RequestQueue.STORAGE_KEY, JSON.stringify(this.queue));
  }
  /**
   * Generate unique ID
   */
  generateId() {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
};
var UsersApi = class {
  constructor(http) {
    this.http = http;
  }
  /**
   * Get current user profile
   */
  async getMe() {
    return this.http.get(API_ENDPOINTS.USERS_ME);
  }
  /**
   * Get user by ID
   */
  async getById(userId) {
    return this.http.get(`${API_ENDPOINTS.USERS}/${userId}`);
  }
  /**
   * Create user (typically called after Keycloak registration)
   */
  async create(data) {
    return this.http.post(API_ENDPOINTS.USERS, data);
  }
  /**
   * Update current user
   */
  async updateMe(data) {
    return this.http.put(API_ENDPOINTS.USERS_ME, data);
  }
  /**
   * Update user by ID (admin only)
   */
  async update(userId, data) {
    return this.http.put(
      `${API_ENDPOINTS.USERS}/${userId}`,
      data
    );
  }
};
var ChildrenApi = class {
  constructor(http) {
    this.http = http;
  }
  /**
   * Get all children for current parent
   */
  async list() {
    return this.http.get(API_ENDPOINTS.CHILDREN);
  }
  /**
   * Get child by ID
   */
  async getById(childId) {
    return this.http.get(`${API_ENDPOINTS.CHILDREN}/${childId}`);
  }
  /**
   * Create child profile
   */
  async create(data) {
    return this.http.post(API_ENDPOINTS.CHILDREN, data);
  }
  /**
   * Update child profile
   */
  async update(childId, data) {
    return this.http.put(
      `${API_ENDPOINTS.CHILDREN}/${childId}`,
      data
    );
  }
  /**
   * Delete child profile
   */
  async delete(childId) {
    return this.http.delete(`${API_ENDPOINTS.CHILDREN}/${childId}`);
  }
};
var AppsApi = class {
  constructor(http) {
    this.http = http;
  }
  /**
   * Get apps for a child
   */
  async getByChild(childId) {
    return this.http.get(`${API_ENDPOINTS.APPS}/child/${childId}`);
  }
  /**
   * Get app by ID
   */
  async getById(appId) {
    return this.http.get(`${API_ENDPOINTS.APPS}/${appId}`);
  }
  /**
   * Register new app
   */
  async register(data) {
    return this.http.post(API_ENDPOINTS.APPS, data);
  }
  /**
   * Update app status
   */
  async updateStatus(appId, data) {
    return this.http.put(
      `${API_ENDPOINTS.APPS}/${appId}/status`,
      data
    );
  }
  /**
   * Set time limit for app
   */
  async setTimeLimit(appId, data) {
    return this.http.put(
      `${API_ENDPOINTS.APPS}/${appId}/time-limit`,
      data
    );
  }
  /**
   * Check app access (for child)
   */
  async checkAccess(packageName) {
    return this.http.get(`${API_ENDPOINTS.APPS_ACCESS}/${packageName}`);
  }
  /**
   * Delete app
   */
  async delete(appId) {
    return this.http.delete(`${API_ENDPOINTS.APPS}/${appId}`);
  }
};
var ExercisesApi = class {
  constructor(http) {
    this.http = http;
  }
  /**
   * Get available exercises with optional filters
   */
  async list(filters, pagination) {
    const params = new URLSearchParams();
    if (filters?.subject) params.append("subject", filters.subject);
    if (filters?.level !== void 0) params.append("level", filters.level.toString());
    if (filters?.difficulty) params.append("difficulty", filters.difficulty);
    if (pagination?.page !== void 0) params.append("page", pagination.page.toString());
    if (pagination?.size !== void 0) params.append("size", pagination.size.toString());
    if (pagination?.sort) params.append("sort", pagination.sort);
    const queryString = params.toString();
    const url = queryString ? `${API_ENDPOINTS.EXERCISES}?${queryString}` : API_ENDPOINTS.EXERCISES;
    return this.http.get(url);
  }
  /**
   * Get random exercise for child level
   */
  async getRandom(level) {
    const url = level ? `${API_ENDPOINTS.EXERCISES}/random?level=${level}` : `${API_ENDPOINTS.EXERCISES}/random`;
    return this.http.get(url);
  }
  /**
   * Get exercise by ID
   */
  async getById(exerciseId) {
    return this.http.get(`${API_ENDPOINTS.EXERCISES}/${exerciseId}`);
  }
  /**
   * Submit answer for exercise
   */
  async submitAnswer(exerciseId, data) {
    return this.http.post(
      `${API_ENDPOINTS.EXERCISES}/${exerciseId}/submit`,
      data
    );
  }
  /**
   * Get exercise results for current child
   */
  async getResults(pagination) {
    const params = new URLSearchParams();
    if (pagination?.page !== void 0) params.append("page", pagination.page.toString());
    if (pagination?.size !== void 0) params.append("size", pagination.size.toString());
    if (pagination?.sort) params.append("sort", pagination.sort);
    const queryString = params.toString();
    const url = queryString ? `${API_ENDPOINTS.EXERCISES_RESULTS}?${queryString}` : API_ENDPOINTS.EXERCISES_RESULTS;
    return this.http.get(url);
  }
  /**
   * Get exercise results for specific child (parent view)
   */
  async getResultsByChild(childId, pagination) {
    const params = new URLSearchParams();
    params.append("childId", childId);
    if (pagination?.size !== void 0) params.append("limit", pagination.size.toString());
    return this.http.get(
      `${API_ENDPOINTS.EXERCISES_RESULTS}?${params.toString()}`
    );
  }
};
var StatsApi = class {
  constructor(http) {
    this.http = http;
  }
  /**
   * Get current child's stats (for child user)
   */
  async getMyStats() {
    return this.http.get(API_ENDPOINTS.STATS_ME);
  }
  /**
   * Get child stats (for parent)
   */
  async getChildStats(childId) {
    return this.http.get(
      API_ENDPOINTS.STATS_CHILD.replace(":childId", childId)
    );
  }
  /**
   * Get daily usage for child
   */
  async getDailyUsage(childId, date) {
    const url = API_ENDPOINTS.STATS_DAILY.replace(":childId", childId);
    return this.http.get(date ? `${url}?date=${date}` : url);
  }
  /**
   * Get weekly usage for child (last 7 days)
   */
  async getWeeklyUsage(childId) {
    return this.http.get(
      `${API_ENDPOINTS.STATS_WEEKLY.replace(":childId", childId)}?days=7`
    );
  }
  /**
   * Get activity history for child
   */
  async getActivities(childId, pagination) {
    const params = new URLSearchParams();
    if (pagination?.page !== void 0) params.append("page", pagination.page.toString());
    if (pagination?.size !== void 0) params.append("size", pagination.size.toString());
    if (pagination?.sort) params.append("sort", pagination.sort);
    const queryString = params.toString();
    const baseUrl = `${API_ENDPOINTS.STATS}/child/${childId}/activities`;
    const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;
    return this.http.get(url);
  }
  /**
   * Record activity (internal use)
   */
  async recordActivity(data) {
    return this.http.post(`${API_ENDPOINTS.STATS}/activity`, data);
  }
};
var AdminApi = class {
  constructor(http) {
    this.http = http;
  }
  // ==================
  // Platform Stats
  // ==================
  /**
   * Get platform statistics
   */
  async getPlatformStats() {
    return this.http.get(API_ENDPOINTS.ADMIN_STATS);
  }
  // ==================
  // User Management
  // ==================
  /**
   * Get all users
   */
  async getUsers(filters, pagination) {
    const params = new URLSearchParams();
    if (filters?.role) params.append("role", filters.role);
    if (filters?.enabled !== void 0) params.append("enabled", filters.enabled.toString());
    if (filters?.search) params.append("search", filters.search);
    if (pagination?.page !== void 0) params.append("page", pagination.page.toString());
    if (pagination?.size !== void 0) params.append("size", pagination.size.toString());
    if (pagination?.sort) params.append("sort", pagination.sort);
    const queryString = params.toString();
    const url = queryString ? `${API_ENDPOINTS.ADMIN_USERS}?${queryString}` : API_ENDPOINTS.ADMIN_USERS;
    return this.http.get(url);
  }
  /**
   * Get user by ID
   */
  async getUserById(userId) {
    return this.http.get(`${API_ENDPOINTS.ADMIN_USERS}/${userId}`);
  }
  /**
   * Toggle user status (enable/disable)
   */
  async toggleUserStatus(userId, data) {
    return this.http.put(
      `${API_ENDPOINTS.ADMIN_USERS}/${userId}/status`,
      data
    );
  }
  // ==================
  // Exercise Management
  // ==================
  /**
   * Get all exercises
   */
  async getExercises(filters, pagination) {
    const params = new URLSearchParams();
    if (filters?.subject) params.append("subject", filters.subject);
    if (filters?.level !== void 0) params.append("level", filters.level.toString());
    if (filters?.difficulty) params.append("difficulty", filters.difficulty);
    if (filters?.enabled !== void 0) params.append("enabled", filters.enabled.toString());
    if (pagination?.page !== void 0) params.append("page", pagination.page.toString());
    if (pagination?.size !== void 0) params.append("size", pagination.size.toString());
    if (pagination?.sort) params.append("sort", pagination.sort);
    const queryString = params.toString();
    const url = queryString ? `${API_ENDPOINTS.ADMIN_EXERCISES}?${queryString}` : API_ENDPOINTS.ADMIN_EXERCISES;
    return this.http.get(url);
  }
  /**
   * Get exercise by ID
   */
  async getExerciseById(exerciseId) {
    return this.http.get(`${API_ENDPOINTS.ADMIN_EXERCISES}/${exerciseId}`);
  }
  /**
   * Create exercise
   */
  async createExercise(data) {
    return this.http.post(API_ENDPOINTS.ADMIN_EXERCISES, data);
  }
  /**
   * Update exercise
   */
  async updateExercise(exerciseId, data) {
    return this.http.put(
      `${API_ENDPOINTS.ADMIN_EXERCISES}/${exerciseId}`,
      data
    );
  }
  /**
   * Toggle exercise status (enable/disable)
   */
  async toggleExerciseStatus(exerciseId, enabled) {
    return this.http.put(
      `${API_ENDPOINTS.ADMIN_EXERCISES}/${exerciseId}/status`,
      { enabled }
    );
  }
  /**
   * Delete exercise
   */
  async deleteExercise(exerciseId) {
    return this.http.delete(`${API_ENDPOINTS.ADMIN_EXERCISES}/${exerciseId}`);
  }
  // ==================
  // Audit Log
  // ==================
  /**
   * Get admin actions (audit log)
   */
  async getAuditLog(pagination) {
    const params = new URLSearchParams();
    if (pagination?.page !== void 0) params.append("page", pagination.page.toString());
    if (pagination?.size !== void 0) params.append("size", pagination.size.toString());
    if (pagination?.sort) params.append("sort", pagination.sort);
    const queryString = params.toString();
    const url = queryString ? `${API_ENDPOINTS.ADMIN_ACTIONS}?${queryString}` : API_ENDPOINTS.ADMIN_ACTIONS;
    return this.http.get(url);
  }
};

// src/client.ts
function createSDKConfigFromApi(apiConfig, options) {
  return {
    baseUrl: apiConfig.baseUrl,
    timeout: apiConfig.timeout,
    ...options
  };
}
var ParentalControlSDK = class {
  httpClient;
  requestQueue = null;
  /** Users API */
  users;
  /** Children API */
  children;
  /** Apps API */
  apps;
  /** Exercises API */
  exercises;
  /** Stats API */
  stats;
  /** Admin API */
  admin;
  constructor(config) {
    this.httpClient = new HttpClient({
      baseUrl: config.baseUrl,
      timeout: config.timeout
    });
    if (config.tokenProvider) {
      this.httpClient.setTokenProvider(config.tokenProvider);
    }
    if (config.onUnauthorized) {
      this.httpClient.setOnUnauthorized(config.onUnauthorized);
    }
    if (config.offlineQueue && config.queueStorage) {
      this.requestQueue = new RequestQueue(config.queueStorage);
    }
    this.users = new UsersApi(this.httpClient);
    this.children = new ChildrenApi(this.httpClient);
    this.apps = new AppsApi(this.httpClient);
    this.exercises = new ExercisesApi(this.httpClient);
    this.stats = new StatsApi(this.httpClient);
    this.admin = new AdminApi(this.httpClient);
  }
  /**
   * Set token provider for authentication
   */
  setTokenProvider(provider) {
    this.httpClient.setTokenProvider(provider);
  }
  /**
   * Set unauthorized callback
   */
  setOnUnauthorized(callback) {
    this.httpClient.setOnUnauthorized(callback);
  }
  /**
   * Initialize offline queue (must be called before using queue features)
   */
  async initializeOfflineQueue() {
    await this.requestQueue?.initialize();
  }
  /**
   * Get offline queue (if enabled)
   */
  getRequestQueue() {
    return this.requestQueue;
  }
  /**
   * Get pending offline requests count
   */
  getPendingRequestsCount() {
    return this.requestQueue?.getPendingCount() ?? 0;
  }
  /**
   * Check if there are pending offline requests
   */
  hasPendingRequests() {
    return this.requestQueue?.hasPending() ?? false;
  }
  /**
   * Process offline queue
   */
  async processOfflineQueue() {
    if (!this.requestQueue) {
      return { success: 0, failed: 0 };
    }
    return this.requestQueue.process(async (request) => {
      const axiosInstance = this.httpClient.getAxiosInstance();
      await axiosInstance.request({
        method: request.method,
        url: request.url,
        data: request.data,
        headers: request.headers
      });
    });
  }
  /**
   * Clear offline queue
   */
  async clearOfflineQueue() {
    await this.requestQueue?.clear();
  }
  /**
   * Get underlying HTTP client
   */
  getHttpClient() {
    return this.httpClient;
  }
};
function createSDK(config) {
  return new ParentalControlSDK(config);
}

export { AdminApi, ApiError, AppsApi, ChildrenApi, ExercisesApi, HttpClient, ParentalControlSDK, RequestQueue, StatsApi, UsersApi, createSDK, createSDKConfigFromApi };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map